/**
 * Settings · Account — OPTIONAL persistent account (cookie-BFF).
 *
 * Susurro is guest-first: without an account, the P2P chat works the same. Here a user
 * CAN create an account (handle + passphrase) to recover their profile on another
 * device. The session token lives in an HttpOnly cookie (outside JS);
 * this screen only calls the {@link useAccount} provider.
 */

import { createSignal, Show } from "solid-js";
import { createRoute, useNavigate } from "@tanstack/solid-router";
import { ArrowLeft, LogOut, ShieldCheck, UserRound } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { AccountError } from "@susurro/protocol";

import { rootRoute } from "@/routes/__root";
import { useAccount } from "@/providers/account-provider";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Atmosphere } from "@/components/ui/atmosphere";
import { ConnectionErrorState } from "@/components/ui/connection-error-state";

const fieldClass =
  "rounded-xl border border-[var(--field-border)] bg-[var(--field-background)] px-3.5 py-2.5 text-sm text-foreground placeholder:text-[var(--field-placeholder)] outline-none transition-shadow focus:ring-2 focus:ring-ring/60";

function SettingsAccountComponent() {
  const account = useAccount();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [mode, setMode] = createSignal<"login" | "register">("register");
  const [handle, setHandle] = createSignal("");
  const [passphrase, setPassphrase] = createSignal("");
  const [displayName, setDisplayName] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);

  const errorFor = (err: unknown): string => {
    if (err instanceof AccountError) {
      if (err.status === 409) {
        return t(msg`That handle is already taken. Try another.`);
      }
      if (err.status === 401) {
        return t(msg`Invalid handle or passphrase.`);
      }
    }
    return t(msg`Something went wrong. Please try again.`);
  };

  const submit = async (event: Event): Promise<void> => {
    event.preventDefault();
    setError(null);
    const h = handle().trim();
    const p = passphrase();
    if (!h || !p) {
      setError(t(msg`Enter a handle and a passphrase.`));
      return;
    }
    try {
      if (mode() === "register") {
        const name = displayName().trim();
        await account.register(h, p, name ? { displayName: name } : {});
      } else {
        await account.login(h, p);
      }
      setPassphrase("");
    } catch (err) {
      setError(errorFor(err));
    }
  };

  return (
    <div class="relative min-h-dvh w-full">
      <Atmosphere />
      <div class="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-5 p-4 sm:p-6">
        <header class="flex items-center gap-3 pt-6 pb-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t(msg`Back`)}
            onClick={() => {
              void navigate({ to: "/" });
            }}
          >
            <ArrowLeft class="h-5 w-5" />
          </Button>
          <div class="flex items-center gap-2">
            <UserRound class="h-5 w-5 text-primary" />
            {/* Ancla de medición de P23/I4: el chrome estático de la pantalla
                (cabecera + volver) NO puede esperar a `GET /api/accounts/me`. */}
            <h1 data-testid="account-chrome-title" class="text-lg font-semibold tracking-tight">
              {t(msg`Account`)}
            </h1>
          </div>
        </header>

        <p class="text-sm text-muted-foreground">
          {t(
            msg`An account is optional. Susurro works without one — your chats stay peer-to-peer and on this device. Add a passphrase-protected account to restore your profile on another device.`,
          )}
        </p>

        {/* P23 / CC-60 · I3 — tres estados, no dos. El relay caído NO puede
            pintarse como «eres invitado»: eso le ofrece crear una cuenta a
            quien ya tiene la suya y le sugiere que la ha perdido. */}
        <Show when={account.error()}>
          <ConnectionErrorState
            testId="account-connection-error"
            title={t(msg`Couldn't check your account`)}
            description={t(
              msg`We couldn't reach the relay, so we don't know whether you're signed in. Your chats keep working — they're peer-to-peer and on this device.`,
            )}
            onRetry={() => account.retry()}
          />
        </Show>

        {/* Envolver, NO encadenar: con `when={!error && !loading}` y
            `fallback={esqueleto}` el fallo pintaría error Y esqueleto a la vez. */}
        <Show when={!account.error()}>
          <Show
            when={!account.loading()}
            fallback={
              <GlassSurface class="flex flex-col gap-4 p-4" data-testid="account-loading">
                <div class="h-9 animate-pulse rounded-xl bg-[var(--surface-secondary)]" />
                <div class="h-16 animate-pulse rounded-xl bg-[var(--surface-secondary)]" />
                <div class="h-9 animate-pulse rounded-xl bg-[var(--surface-secondary)]" />
              </GlassSurface>
            }
          >
            <Show
              when={account.session()}
              fallback={
                <GlassSurface class="flex flex-col gap-4 p-4">
                  <div class="flex gap-2" role="tablist" aria-label={t(msg`Account action`)}>
                    <Button
                      variant={mode() === "register" ? "default" : "outline"}
                      size="sm"
                      class="flex-1"
                      data-testid="account-mode-register"
                      onClick={() => {
                        setMode("register");
                        setError(null);
                      }}
                    >
                      {t(msg`Create account`)}
                    </Button>
                    <Button
                      variant={mode() === "login" ? "default" : "outline"}
                      size="sm"
                      class="flex-1"
                      data-testid="account-mode-login"
                      onClick={() => {
                        setMode("login");
                        setError(null);
                      }}
                    >
                      {t(msg`Sign in`)}
                    </Button>
                  </div>

                  <form class="flex flex-col gap-3" onSubmit={(e) => void submit(e)}>
                    <label class="flex flex-col gap-1.5">
                      <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                        {t(msg`Handle`)}
                      </span>
                      <input
                        class={fieldClass}
                        data-testid="account-handle-input"
                        autocomplete="username"
                        value={handle()}
                        placeholder={t(msg`e.g. alice`)}
                        onInput={(e) => setHandle(e.currentTarget.value)}
                      />
                    </label>

                    <Show when={mode() === "register"}>
                      <label class="flex flex-col gap-1.5">
                        <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                          {t(msg`Display name (optional)`)}
                        </span>
                        <input
                          class={fieldClass}
                          data-testid="account-name-input"
                          value={displayName()}
                          placeholder={t(msg`e.g. Alice`)}
                          onInput={(e) => setDisplayName(e.currentTarget.value)}
                        />
                      </label>
                    </Show>

                    <label class="flex flex-col gap-1.5">
                      <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                        {t(msg`Passphrase`)}
                      </span>
                      <input
                        class={fieldClass}
                        data-testid="account-passphrase-input"
                        type="password"
                        autocomplete={mode() === "register" ? "new-password" : "current-password"}
                        value={passphrase()}
                        placeholder={t(msg`A phrase only you know`)}
                        onInput={(e) => setPassphrase(e.currentTarget.value)}
                      />
                    </label>

                    <Show when={error()}>
                      <p data-testid="account-error" class="text-sm text-destructive" role="alert">
                        {error()}
                      </p>
                    </Show>

                    <Button
                      type="submit"
                      class="gap-2"
                      data-testid="account-submit"
                      disabled={account.busy()}
                    >
                      <ShieldCheck class="h-4 w-4" />
                      <Show when={mode() === "register"} fallback={t(msg`Sign in`)}>
                        {t(msg`Create account`)}
                      </Show>
                    </Button>
                  </form>
                </GlassSurface>
              }
            >
              {(session) => (
                <GlassSurface class="flex flex-col gap-4 p-4">
                  <div class="flex items-center gap-3">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <UserRound class="h-5 w-5" />
                    </div>
                    <div class="flex flex-col">
                      <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                        {t(msg`Signed in as`)}
                      </span>
                      <span
                        data-testid="account-signed-in"
                        class="text-sm font-medium text-foreground"
                      >
                        {session().handle}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    class="gap-2"
                    data-testid="account-logout"
                    disabled={account.busy()}
                    onClick={() => void account.logout()}
                  >
                    <LogOut class="h-4 w-4" />
                    {t(msg`Sign out`)}
                  </Button>
                </GlassSurface>
              )}
            </Show>
          </Show>
        </Show>
      </div>
    </div>
  );
}

export const settingsAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/account",
  component: SettingsAccountComponent,
});
